// frontend/src/contexts/AuditContext.js
import React, { createContext, useContext, useCallback, useState } from "react";
import secureAxios from "../utils/secureAxios";

export const AuditContext = createContext();

// Helper function to validate and sanitize UUID values
const sanitizeUUID = (value) => {
  if (!value || value === "" || value === "undefined" || value === "null") {
    return null;
  }

  // Check if it's a valid UUID format
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  if (typeof value === "string" && uuidRegex.test(value)) {
    return value;
  }

  return null;
};

export const AuditProvider = ({ children }) => {
  const [currentAuditContext, setCurrentAuditContext] = useState(null);

  /**
   * Set audit headers for PHI access
   * This should be called before accessing any PHI data
   *
   * @param {Object} auditInfo
   * @param {string} auditInfo.reason - Reason for accessing PHI
   * @param {string} auditInfo.purpose_of_use - Purpose code (TREATMENT, PAYMENT, OPERATIONS, etc.)
   * @param {string} auditInfo.target_type - Type of resource being accessed (PATIENT, DOCUMENT, etc.)
   * @param {string} auditInfo.target_id - ID of the specific resource
   * @param {string} auditInfo.patient_id - Patient ID if accessing patient data
   */
  const setAuditHeaders = useCallback(async (auditInfo) => {
    try {
      // Validate required fields
      if (!auditInfo.reason || !auditInfo.purpose_of_use) {
        throw new Error(
          "Audit reason and purpose of use are required for PHI access"
        );
      }

      // Store current audit context
      setCurrentAuditContext(auditInfo);

      // Sanitize UUID fields before sending
      const sanitizedTargetId = sanitizeUUID(auditInfo.target_id);
      const sanitizedPatientId = sanitizeUUID(auditInfo.patient_id);

      console.log("🔍 AuditContext debug:", {
        target_id: auditInfo.target_id,
        sanitizedTargetId: sanitizedTargetId,
        patient_id: auditInfo.patient_id,
        sanitizedPatientId: sanitizedPatientId,
      });

      // Log the audit event to the backend using the CORRECT endpoint
      const auditPayload = {
        action: "PHI_ACCESS",
        entity_type: auditInfo.target_type || "PHI",
        entity_id: sanitizedTargetId,
        entity_identifier: auditInfo.reason,
        action_reason: auditInfo.reason,
        purpose_of_use: auditInfo.purpose_of_use,
        contains_phi: true,
        data_sensitivity_level: "high",
        access_time: new Date().toISOString(),
      };

      // FIXED: Use the correct endpoint that matches server.js route registration
      await secureAxios.post("/api/audit-logs/log", auditPayload);

      // Set headers for subsequent requests
      secureAxios.defaults.headers.common["X-Audit-Reason"] = auditInfo.reason;
      secureAxios.defaults.headers.common["X-Audit-Purpose"] =
        auditInfo.purpose_of_use;
      secureAxios.defaults.headers.common["X-Audit-Target-Type"] =
        auditInfo.target_type;
      secureAxios.defaults.headers.common["X-Audit-Target-Id"] =
        sanitizedTargetId || "";
      if (sanitizedPatientId) {
        secureAxios.defaults.headers.common["X-Audit-Patient-Id"] =
          sanitizedPatientId;
      }

      console.log("✅ Audit context set for PHI access");
    } catch (err) {
      console.error("❌ Failed to set audit context:", err);
      throw err; // Re-throw to prevent PHI access without audit
    }
  }, []);

  /**
   * Clear audit headers after PHI access is complete
   */
  const clearAuditHeaders = useCallback(() => {
    setCurrentAuditContext(null);

    // Remove audit headers
    delete secureAxios.defaults.headers.common["X-Audit-Reason"];
    delete secureAxios.defaults.headers.common["X-Audit-Purpose"];
    delete secureAxios.defaults.headers.common["X-Audit-Target-Type"];
    delete secureAxios.defaults.headers.common["X-Audit-Target-Id"];
    delete secureAxios.defaults.headers.common["X-Audit-Patient-Id"];

    console.log("✅ Audit context cleared");
  }, []);

  /**
   * Log a specific audit event
   * Use this for important actions that need explicit audit logging
   *
   * @param {Object} event
   * @param {string} event.action - Action being performed
   * @param {string} event.entity_type - Type of resource (formerly resource_type)
   * @param {string} event.entity_id - ID of resource (formerly resource_id)
   * @param {Object} event.details - Additional details
   */
  const logAuditEvent = useCallback(async (event) => {
    try {
      // Sanitize UUID fields
      const sanitizedEntityId = sanitizeUUID(
        event.entity_id || event.resource_id
      );

      // Map old field names to new ones for backward compatibility
      const auditPayload = {
        action: event.action,
        entity_type: event.entity_type || event.resource_type || "UNKNOWN",
        entity_id: sanitizedEntityId,
        entity_identifier: event.entity_identifier || event.description,
        action_status: event.action_status || "SUCCESS",
        action_reason: event.action_reason || event.reason,
        purpose_of_use: event.purpose_of_use,
        contains_phi: event.contains_phi || false,
        data_sensitivity_level: event.data_sensitivity_level || "medium",
        old_values: event.old_values,
        new_values: event.new_values || event.details,
        fields_accessed: event.fields_accessed || [],
        phi_types: event.phi_types || [],
        timestamp: event.timestamp || new Date().toISOString(),
      };

      console.log("🔍 Logging audit event:", auditPayload);

      // FIXED: Use the correct endpoint that matches server.js route registration
      await secureAxios.post("/api/audit-logs/log", auditPayload);
    } catch (err) {
      console.error("❌ Failed to log audit event:", err);
      // Don't throw here - logging failure shouldn't break the app
    }
  }, []);

  /**
   * Wrapper for PHI access that ensures audit context is set
   *
   * @param {Function} apiCall - The API call function to execute
   * @param {Object} auditInfo - Audit information for the access
   * @returns {Promise} Result of the API call
   */
  const withAuditContext = useCallback(
    async (apiCall, auditInfo) => {
      try {
        // Set audit context
        await setAuditHeaders(auditInfo);

        // Execute the API call
        const result = await apiCall();

        // Log successful access
        await logAuditEvent({
          action: "PHI_ACCESS_SUCCESS",
          entity_type: auditInfo.target_type,
          entity_id: auditInfo.target_id,
          entity_identifier: `Successful PHI access: ${auditInfo.reason}`,
          contains_phi: true,
          purpose_of_use: auditInfo.purpose_of_use,
          action_status: "SUCCESS",
        });

        return result;
      } catch (err) {
        // Log failed access
        await logAuditEvent({
          action: "PHI_ACCESS_FAILED",
          entity_type: auditInfo.target_type,
          entity_id: auditInfo.target_id,
          entity_identifier: `Failed PHI access: ${auditInfo.reason}`,
          action_reason: err.message,
          contains_phi: true,
          purpose_of_use: auditInfo.purpose_of_use,
          action_status: "FAILED",
        });

        throw err;
      } finally {
        // Always clear audit context
        clearAuditHeaders();
      }
    },
    [setAuditHeaders, logAuditEvent, clearAuditHeaders]
  );

  /**
   * Safe wrapper for logging general actions
   *
   * @param {string} action - Action being performed
   * @param {string} entityType - Type of entity
   * @param {string} entityId - ID of entity (will be sanitized)
   * @param {Object} details - Additional details
   */
  const logActionSafe = useCallback(
    async (action, entityType, entityId, details = {}) => {
      await logAuditEvent({
        action,
        entity_type: entityType,
        entity_id: entityId, // Will be sanitized in logAuditEvent
        entity_identifier: details.description || `${action} on ${entityType}`,
        action_status: details.status || "SUCCESS",
        action_reason: details.reason,
        contains_phi: details.contains_phi || false,
        data_sensitivity_level: details.sensitivity || "medium",
        new_values: details,
      });
    },
    [logAuditEvent]
  );

  /**
   * Safe wrapper for logging PHI access actions
   *
   * @param {string} action - Action being performed
   * @param {string} entityType - Type of entity
   * @param {string} entityId - ID of entity (will be sanitized)
   * @param {string} purpose - Purpose of use
   * @param {Object} details - Additional details
   */
  const logPHIAccess = useCallback(
    async (action, entityType, entityId, purpose, details = {}) => {
      await logAuditEvent({
        action,
        entity_type: entityType,
        entity_id: entityId, // Will be sanitized in logAuditEvent
        entity_identifier:
          details.description || `PHI ${action} on ${entityType}`,
        purpose_of_use: purpose,
        contains_phi: true,
        data_sensitivity_level: "high",
        action_status: details.status || "SUCCESS",
        action_reason: details.reason,
        fields_accessed: details.fields || [],
        phi_types: details.phi_types || ["medical_information"],
        new_values: details,
      });
    },
    [logAuditEvent]
  );

  // Purpose of use constants for HIPAA compliance
  const PURPOSES = {
    TREATMENT: "TREATMENT",
    PAYMENT: "PAYMENT",
    OPERATIONS: "OPERATIONS",
    RESEARCH: "RESEARCH",
    PUBLIC_HEALTH: "PUBLIC_HEALTH",
    DIRECTORY: "DIRECTORY",
    EMERGENCY: "EMERGENCY",
  };

  // Resource/Entity type constants
  const RESOURCE_TYPES = {
    CONSUMER: "consumers",
    USER: "users",
    AUTHORIZATION: "authorizations",
    JOB_LOG: "job_logs",
    INVOICE: "invoices",
    PLAN: "plans",
    DOCUMENT: "documents",
    ROUTE: "routes",
    PHI: "PHI",
    AUDIT_LOG: "audit_logs",
    SYSTEM: "system",
  };

  const value = {
    currentAuditContext,
    setAuditHeaders,
    clearAuditHeaders,
    logAuditEvent,
    withAuditContext,
    logActionSafe,
    logPHIAccess,
    PURPOSES,
    RESOURCE_TYPES,
  };

  return (
    <AuditContext.Provider value={value}>{children}</AuditContext.Provider>
  );
};

export const useAuditContext = () => {
  const context = useContext(AuditContext);
  if (!context) {
    throw new Error("useAuditContext must be used within an AuditProvider");
  }
  return context;
};
